import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, HandHeart, HandCoins, Package, Building2, Sparkles, Lock, CreditCard, ExternalLink } from "lucide-react";
import { PageHero } from "@/pages/About";
import { Field, TextInput, TextArea, SelectField, ConsentCheckbox, Honeypot } from "@/components/FormFields";
import { useFormSubmit } from "@/lib/useFormSubmit";
import { SuccessCard } from "@/pages/WarrenParkConnect";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { trackEvent } from "@/lib/analytics";

const ECOCASH = "078 265 8473";
const ECOCASH_INTL = "+263 78 265 8473";
const WHATSAPP_URL = "https://wa.me/263782658473?text=" + encodeURIComponent(
    "Hello Global Seed Foundation. I have made an EcoCash donation and would like to submit my payment confirmation."
);
const CURRENCIES = ["USD", "ZAR", "ZWL", "EUR", "GBP", "Other"];
const PROGRAMMES = [
    "General support", "Community Enterprise Development", "Women Empowerment",
    "Youth Empowerment", "Disability Inclusion", "Skills Development",
    "Community Infrastructure", "Community Listening Forums",
    "Humanitarian & Emergency Family Support",
];

const DONATION_PRESETS = [10, 25, 50, 100, 250, 500, 1000];

export default function Donate() {
    const [tab, setTab] = useState(null);
    const [donationAmount, setDonationAmount] = useState(null);
    const [donationFrequency, setDonationFrequency] = useState("one_time");
    const panelRef = useRef(null);
    const paymentRef = useRef(null);
    const isFirstRender = useRef(true);

    // Donation page view — fire once on mount.
    useEffect(() => { trackEvent("donate_page_view"); }, []);

    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        const el = panelRef.current;
        if (!el || !tab) return;
        try { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
        catch { el.scrollIntoView(); }
        const t = setTimeout(() => {
            const heading = el.querySelector("[data-panel-heading]");
            if (heading && typeof heading.focus === "function") heading.focus({ preventScroll: true });
        }, 500);
        return () => clearTimeout(t);
    }, [tab]);

    // When a donation amount is chosen, reveal + smooth-scroll to the payment-method step.
    useEffect(() => {
        if (donationAmount == null) return;
        const el = paymentRef.current;
        if (!el) return;
        try { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
        catch { el.scrollIntoView(); }
        const t = setTimeout(() => {
            const heading = el.querySelector("[data-panel-heading]");
            if (heading && typeof heading.focus === "function") heading.focus({ preventScroll: true });
        }, 500);
        return () => clearTimeout(t);
    }, [donationAmount]);

    return (
        <div data-testid="donate-page">
            <PageHero title="Ways to Support Global Seed Foundation" subtitle="Donate · Volunteer · Partner · Pledge" />

            {/* NEW — Step 1 & 2: frequency + amount */}
            <DonationAmountStep
                frequency={donationFrequency}
                setFrequency={setDonationFrequency}
                amount={donationAmount}
                setAmount={setDonationAmount}
            />

            {/* NEW — Step 3: payment methods (revealed only after amount selected) */}
            {donationAmount != null && (
                <section
                    ref={paymentRef}
                    id="payment-methods"
                    className="bg-white gsf-section border-t border-[#eef0ee]"
                    style={{ scrollMarginTop: "120px" }}
                    data-testid="payment-methods-step"
                    aria-live="polite"
                >
                    <div className="gsf-container">
                        <div className="max-w-3xl">
                            <div className="gsf-eyebrow">Step 3 · Payment method</div>
                            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[color:var(--gsf-green-700)] font-bold outline-none" data-panel-heading tabIndex={-1}>
                                Choose how you'd like to send US${donationAmount.toLocaleString()} ({donationFrequency === "one_time" ? "one-time" : "monthly"})
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                <button onClick={() => setDonationAmount(null)} className="underline text-[color:var(--gsf-green-700)]" data-testid="change-amount-btn">
                                    ← Change amount or frequency
                                </button>
                            </p>
                        </div>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payment-cards">
                            <SupportCard testid="pay-ecocash" active={tab === "ecocash"} onClick={() => { setTab("ecocash"); trackEvent("payment_method_selected", { method: "ecocash", amount: donationAmount, frequency: donationFrequency }); }}
                                icon={HandCoins} title="EcoCash"
                                body="Send your donation via EcoCash to 078 265 8473 and submit the confirmation form." />
                            <SupportCard testid="pay-paypal"
                                icon={CreditCard} title="PayPal / Card"
                                body="Secure PayPal and Visa/Mastercard payments — activating once verification and testing are complete." comingSoon />
                        </div>
                    </div>
                </section>
            )}

            <section className="gsf-section" data-testid="ways-to-support">
                <div className="gsf-container">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow">Other Ways to Support</div>
                        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--gsf-green-700)] font-bold">
                            Prefer to support in another way?
                        </h2>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            Volunteer your skills, donate goods, partner with us as an organisation, or register your interest. Every contribution helps.
                        </p>
                    </div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="support-cards" role="tablist" aria-label="Other ways to support">
                        <SupportCard testid="opt-volunteer" active={tab === "volunteer"} onClick={() => setTab("volunteer")}
                            icon={HandHeart} title="Volunteer Your Skills"
                            body="Support community development through your time, professional skills and experience." />
                        <SupportCard testid="opt-goods" active={tab === "goods"} onClick={() => setTab("goods")}
                            icon={Package} title="Donate Goods or Equipment"
                            body="Support communities by donating practical items that strengthen livelihoods and improve lives." />
                        <SupportCard testid="opt-corporate" active={tab === "corporate"} onClick={() => setTab("corporate")}
                            icon={Building2} title="Partner With Global Seed Foundation"
                            body="Organisations can support sustainable community development through strategic partnerships." />
                        <SupportCard testid="opt-pledge" active={tab === "pledge"} onClick={() => setTab("pledge")}
                            icon={Sparkles} title="I'd Like to Support"
                            body="Not ready to donate today? Let us know your interest and we'll be in touch." />
                    </div>
                </div>
            </section>

            {tab && (
                <section
                    ref={panelRef}
                    id="support-panel"
                    className="bg-[color:var(--gsf-soft-green)] gsf-section"
                    style={{ scrollMarginTop: "140px" }}
                    data-testid="support-panel"
                    aria-live="polite"
                    role="region"
                    aria-label="Selected support option"
                >
                    <div className="gsf-container">
                        {tab === "ecocash" && <EcocashPanel initialAmount={donationAmount} initialFrequency={donationFrequency} />}
                        {tab === "volunteer" && <VolunteerPanel />}
                        {tab === "goods" && <GoodsPanel />}
                        {tab === "corporate" && <CorporatePanel />}
                        {tab === "pledge" && <PledgePanel />}
                    </div>
                </section>
            )}
        </div>
    );
}

function DonationAmountStep({ frequency, setFrequency, amount, setAmount }) {
    const [other, setOther] = useState("");
    const [preset, setPreset] = useState(null);

    function pick(v) {
        setAmount(v);
        setPreset(v);
        setOther("");
        trackEvent("amount_selected", { amount: v, custom: false });
    }
    function applyOther() {
        const n = parseFloat(other);
        if (!isFinite(n) || n < 1) {
            toast.error("Please enter a valid donation amount of at least US$1.");
            trackEvent("form_validation_failed", { field: "amount_other", value: other });
            return;
        }
        const rounded = Math.round(n * 100) / 100;
        setAmount(rounded);
        setPreset("other");
        trackEvent("amount_selected", { amount: rounded, custom: true });
    }

    return (
        <section className="gsf-section" data-testid="donation-amount-step">
            <div className="gsf-container">
                <div className="max-w-3xl">
                    <div className="gsf-eyebrow">Step 1 · Frequency</div>
                    <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[color:var(--gsf-green-700)] font-bold">Choose your donation</h2>
                    <p className="mt-2 text-sm text-gray-600">Amounts are shown in United States Dollars (USD).</p>
                </div>

                <div className="mt-6 inline-flex rounded-full border border-[#e6ebe4] p-1 bg-white" role="group" aria-label="Donation frequency" data-testid="frequency-toggle">
                    <button onClick={() => { setFrequency("one_time"); trackEvent("frequency_selected", { frequency: "one_time" }); }}
                        aria-pressed={frequency === "one_time"}
                        data-testid="freq-one-time"
                        className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${frequency === "one_time" ? "bg-[color:var(--gsf-green-700)] text-white" : "text-[color:var(--gsf-charcoal)] hover:bg-[color:var(--gsf-soft-green)]"}`}>
                        One-Time Donation
                    </button>
                    <button disabled title="Monthly recurring becomes available once PayPal is activated"
                        data-testid="freq-monthly"
                        aria-disabled="true"
                        className="px-5 py-2 rounded-full text-sm font-medium text-gray-400 cursor-not-allowed">
                        Monthly Support <span className="ml-1 text-[10px]">(coming soon)</span>
                    </button>
                </div>

                <div className="mt-8">
                    <div className="gsf-eyebrow">Step 2 · Amount (USD)</div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="amount-grid">
                        {DONATION_PRESETS.map((v) => {
                            const active = amount === v && preset !== "other";
                            return (
                                <button key={v} type="button" onClick={() => pick(v)}
                                    data-testid={`amount-${v}`}
                                    aria-pressed={active}
                                    className={`px-4 py-4 rounded-xl border text-lg font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[color:var(--gsf-gold-500)] focus:ring-offset-2 ${active ? "bg-[color:var(--gsf-green-700)] text-white border-[color:var(--gsf-green-700)]" : "bg-white text-[color:var(--gsf-charcoal)] border-[#e6ebe4] hover:border-[color:var(--gsf-green-600)]"}`}>
                                    US${v.toLocaleString()}
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">US$</span>
                            <input inputMode="decimal" type="number" min="1" step="1"
                                value={other}
                                onChange={(e) => setOther(e.target.value)}
                                placeholder="Other amount"
                                data-testid="amount-other-input"
                                className="w-full pl-11 pr-3 py-3 rounded-xl border border-[#e6ebe4] bg-white text-base focus:outline-none focus:ring-2 focus:ring-[color:var(--gsf-gold-500)]" />
                        </div>
                        <button type="button" onClick={applyOther} data-testid="amount-other-apply"
                            className="gsf-btn-primary text-sm px-6">
                            Apply
                        </button>
                    </div>
                    {amount != null && (
                        <p className="mt-4 text-sm" data-testid="donation-selected-summary">
                            Selected: <b className="text-[color:var(--gsf-green-700)]" data-testid="donation-selected-amount">US${amount.toLocaleString()}</b>
                            {" · "}
                            <b className="text-[color:var(--gsf-green-700)]" data-testid="donation-selected-frequency">
                                {frequency === "one_time" ? "One-Time" : "Monthly"}
                            </b>
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}

function SupportCard({ icon: Icon, title, body, active, onClick, comingSoon, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            role="tab"
            aria-selected={active}
            aria-controls="support-panel"
            className={`gsf-card text-left p-6 h-full flex flex-col transition-all group focus:outline-none focus:ring-2 focus:ring-[color:var(--gsf-gold-500)] focus:ring-offset-2 ${active ? "ring-2 ring-[color:var(--gsf-gold-500)] bg-[color:var(--gsf-soft-green)]/60" : "hover:-translate-y-0.5 hover:shadow-lg"}`}
        >
            <div className="w-11 h-11 rounded-xl bg-[color:var(--gsf-soft-green)] flex items-center justify-center text-[color:var(--gsf-green-700)]">
                <Icon size={22} strokeWidth={1.7} />
            </div>
            <h3 className="mt-4 font-serif text-lg font-semibold text-[color:var(--gsf-green-700)]">{title}</h3>
            <p className="mt-2 text-sm text-[color:var(--gsf-body)] flex-1">{body}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                {active && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[color:var(--gsf-green-700)] text-white" data-testid={`${testid}-selected`}>
                        <Check size={10} /> Selected
                    </span>
                )}
                {comingSoon && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[color:var(--gsf-gold-500)]/25 text-[color:var(--gsf-green-700)]" data-testid="online-coming-soon">
                        <Lock size={10} /> Coming soon
                    </span>
                )}
            </div>
        </button>
    );
}

function PanelHeader({ eyebrow, title, children }) {
    return (
        <div className="max-w-3xl">
            <div className="gsf-eyebrow">{eyebrow}</div>
            <h2
                className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold outline-none"
                data-panel-heading
                tabIndex={-1}
            >
                {title}
            </h2>
            {children && <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">{children}</p>}
        </div>
    );
}

/* ---------------- EcoCash ---------------- */
function EcocashPanel({ initialAmount = null, initialFrequency = "one_time" }) {
    const [copied, setCopied] = useState(false);
    async function copy() {
        try { await navigator.clipboard.writeText(ECOCASH); setCopied(true); toast.success("EcoCash number copied"); setTimeout(() => setCopied(false), 2000); }
        catch { toast.error("Please copy the number manually"); }
    }
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div>
                <PanelHeader eyebrow="EcoCash" title="Donate via EcoCash">
                    Send your donation to the EcoCash number below, then complete the confirmation form so our team can verify and acknowledge it.
                </PanelHeader>
                <div className="mt-6 gsf-card p-6" data-testid="ecocash-card">
                    <dl className="divide-y divide-[#eef0ee]">
                        <div className="py-3 flex justify-between"><dt className="text-sm text-gray-500">EcoCash Number</dt><dd className="font-mono font-bold text-xl text-[color:var(--gsf-green-700)]" data-testid="ecocash-number">{ECOCASH_INTL}</dd></div>
                        <div className="py-3 flex justify-between"><dt className="text-sm text-gray-500">Local Format</dt><dd className="font-mono" data-testid="ecocash-local">{ECOCASH}</dd></div>
                        <div className="py-3 flex justify-between"><dt className="text-sm text-gray-500">Recipient</dt><dd>Global Seed Foundation</dd></div>
                        {initialAmount != null && (
                            <div className="py-3 flex justify-between"><dt className="text-sm text-gray-500">Your donation</dt><dd className="font-semibold text-[color:var(--gsf-green-700)]" data-testid="ecocash-selected-summary">US${initialAmount.toLocaleString()} · {initialFrequency === "one_time" ? "One-Time" : "Monthly"}</dd></div>
                        )}
                    </dl>
                    <ol className="mt-5 text-sm text-gray-700 space-y-1 list-decimal ml-4">
                        <li>Send the EcoCash payment to the number above.</li>
                        <li>Keep the transaction reference from the confirmation SMS.</li>
                        <li>Complete the confirmation form on the right.</li>
                        <li>Submit — we will verify and acknowledge your donation.</li>
                    </ol>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <button onClick={copy} className="gsf-btn-outline text-sm" data-testid="copy-ecocash-btn">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy EcoCash Number"}</button>
                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="gsf-btn-ghost text-sm" data-testid="ecocash-whatsapp-btn"><WhatsAppIcon size={14} /> Notify via WhatsApp</a>
                    </div>
                </div>
            </div>
            <div id="confirm">
                <PanelHeader eyebrow="Confirmation" title="Notify Us of Your Donation">After sending EcoCash, please complete this confirmation form.</PanelHeader>
                <EcocashForm initialAmount={initialAmount} initialFrequency={initialFrequency} />
            </div>
        </div>
    );
}
function EcocashForm({ initialAmount = null, initialFrequency = "one_time" }) {
    const [s, setS] = useState({
        full_name: "", email: "", phone: "",
        amount: initialAmount != null ? String(initialAmount) : "",
        currency: "USD",
        frequency: initialFrequency,
        donation_date: "", ecocash_reference: "", programme: "", message: "", website: "",
    });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("ecocash_donation");
    // Batch 21 — track programme attribution (invisible, no UI change) whenever
    // the donor selects a programme so executive intelligence can attribute
    // donation intent to the correct programme.
    useEffect(() => {
        if (s.programme) trackEvent("programme_interest", { programme: s.programme });
    }, [s.programme]);
    // Fire confirmation_submitted the moment the panel switches to success —
    // this closes the funnel for the executive dashboard. No visible change.
    useEffect(() => {
        if (done) {
            trackEvent("confirmation_submitted", {
                method: "ecocash",
                amount: initialAmount || undefined,
                frequency: initialFrequency,
            });
        }
    }, [done, initialAmount, initialFrequency]);
    if (done) return <SuccessCard testid="ecocash-success">Thank you for supporting Global Seed Foundation. Your donation details have been received for verification and our team will acknowledge them shortly.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-6 space-y-4 mt-6" data-testid="ecocash-form">
            <Field id="ec-name" label="Full name" required><TextInput id="ec-name" testid="ec-name" required value={s.full_name} onChange={(v) => setS({ ...s, full_name: v })} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="ec-email" label="Email (optional)"><TextInput id="ec-email" testid="ec-email" type="email" value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="ec-phone" label="Phone number" required><TextInput id="ec-phone" testid="ec-phone" required value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
                <Field id="ec-amt" label="Amount" required><TextInput id="ec-amt" testid="ec-amt" required value={s.amount} onChange={(v) => setS({ ...s, amount: v })} /></Field>
                <Field id="ec-cur" label="Currency"><SelectField id="ec-cur" testid="ec-cur" value={s.currency} onChange={(v) => setS({ ...s, currency: v })} options={CURRENCIES} placeholder="Currency" /></Field>
                <Field id="ec-date" label="Date of donation" required><TextInput id="ec-date" testid="ec-date" type="date" required value={s.donation_date} onChange={(v) => setS({ ...s, donation_date: v })} /></Field>
                <Field id="ec-ref" label="EcoCash reference" required><TextInput id="ec-ref" testid="ec-ref" required value={s.ecocash_reference} onChange={(v) => setS({ ...s, ecocash_reference: v })} /></Field>
            </div>
            <Field id="ec-prog" label="Programme supported"><SelectField id="ec-prog" testid="ec-prog" value={s.programme} onChange={(v) => setS({ ...s, programme: v })} options={PROGRAMMES} placeholder="Select programme" /></Field>
            <Field id="ec-msg" label="Optional message"><TextArea id="ec-msg" testid="ec-msg" rows={3} value={s.message} onChange={(v) => setS({ ...s, message: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="ec-consent" />
            {error && <p className="text-sm text-red-600" data-testid="ec-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="ec-submit">{submitting ? "Submitting…" : "Confirm My Donation"}</button>
        </form>
    );
}

/* ---------------- Volunteer ---------------- */
const DONATE_VOLUNTEER_PROGRAMMES = [
    "Community Engagement",
    "Youth Empowerment",
    "Women Empowerment",
    "Persons with Disabilities Support",
    "Community Enterprise Development",
    "Education & Skills Development",
    "Environmental & Community Clean-up",
    "Food & Livelihood Support",
    "Events & Outreach",
    "Administration & Office Support",
    "ICT & Digital Support",
    "Communications, Photography & Media",
    "Fundraising & Resource Mobilisation",
    "Monitoring & Evaluation",
    "Any Programme (I'm happy to help where needed)",
];

function VolunteerPanel() {
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div>
                <PanelHeader eyebrow="Volunteer" title="Volunteer Your Skills">Support community development through your time, professional skills and experience.</PanelHeader>
                <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-[color:var(--gsf-body)]" data-testid="volunteer-examples">
                    {["Mentorship", "Skills training", "Administration", "Community outreach", "Youth programmes", "Women's empowerment", "Disability support", "Events", "Photography", "Videography"].map((x) => <li key={x}>• {x}</li>)}
                </ul>
            </div>
            <VolunteerForm />
        </div>
    );
}
function VolunteerForm() {
    const [s, setS] = useState({ full_name: "", email: "", phone: "", preferred_programme: "", skills: "", availability: "", motivation: "", other_interests: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("volunteer_application");
    if (done) return <SuccessCard testid="volunteer-success">Thank you for volunteering. Our team will contact you shortly.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-6 space-y-4" data-testid="volunteer-form">
            <Field id="v-name" label="Full name" required><TextInput id="v-name" testid="v-name" required value={s.full_name} onChange={(v) => setS({ ...s, full_name: v })} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="v-email" label="Email" required><TextInput id="v-email" testid="v-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="v-phone" label="Phone" required><TextInput id="v-phone" testid="v-phone" required value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
            </div>
            <Field id="v-prog" label="Preferred programme" required>
                <SelectField id="v-prog" testid="v-programme" value={s.preferred_programme} onChange={(v) => setS({ ...s, preferred_programme: v })} placeholder="Select a programme"
                    options={DONATE_VOLUNTEER_PROGRAMMES} />
            </Field>
            <Field id="v-skills" label="Skills" required><TextInput id="v-skills" testid="v-skills" required value={s.skills} onChange={(v) => setS({ ...s, skills: v })} /></Field>
            <Field id="v-avail" label="Availability" required><TextInput id="v-avail" testid="v-availability" required placeholder="e.g. Weekends, 4 hours per week" value={s.availability} onChange={(v) => setS({ ...s, availability: v })} /></Field>
            <Field id="v-other" label="Other interests, skills or experience"><TextArea id="v-other" testid="v-other-interests" rows={3} value={s.other_interests} onChange={(v) => setS({ ...s, other_interests: v })} /></Field>
            <Field id="v-msg" label="Motivation"><TextArea id="v-msg" testid="v-motivation" rows={3} value={s.motivation} onChange={(v) => setS({ ...s, motivation: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="v-consent" />
            {error && <p className="text-sm text-red-600" data-testid="v-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="v-submit">{submitting ? "Submitting…" : "Become a Volunteer"}</button>
        </form>
    );
}

/* ---------------- Goods ---------------- */
function GoodsPanel() {
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div>
                <PanelHeader eyebrow="Goods & Equipment" title="Donate Goods or Equipment">Support communities by donating practical items that strengthen livelihoods and improve lives.</PanelHeader>
                <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-[color:var(--gsf-body)]" data-testid="goods-examples">
                    {["Wheelchairs", "Walking aids", "Food hampers", "School supplies", "Sewing machines", "Welding equipment", "Broiler equipment", "Fabric", "Cleaning material inputs", "Office equipment", "ICT equipment"].map((x) => <li key={x}>• {x}</li>)}
                </ul>
            </div>
            <GoodsForm />
        </div>
    );
}
function GoodsForm() {
    const [s, setS] = useState({ donor_name: "", email: "", phone: "", item_description: "", quantity: "", condition: "New", collection_location: "", contact_method: "Email", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("goods_donation");
    if (done) return <SuccessCard testid="goods-success">Thank you. Our team will confirm collection or drop-off arrangements shortly.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-6 space-y-4" data-testid="goods-form">
            <Field id="g-name" label="Donor name" required><TextInput id="g-name" testid="g-name" required value={s.donor_name} onChange={(v) => setS({ ...s, donor_name: v })} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="g-email" label="Email" required><TextInput id="g-email" testid="g-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="g-phone" label="Phone" required><TextInput id="g-phone" testid="g-phone" required value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
            </div>
            <Field id="g-item" label="Item description" required><TextArea id="g-item" testid="g-item" rows={2} required value={s.item_description} onChange={(v) => setS({ ...s, item_description: v })} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="g-qty" label="Quantity" required><TextInput id="g-qty" testid="g-qty" required value={s.quantity} onChange={(v) => setS({ ...s, quantity: v })} /></Field>
                <Field id="g-cond" label="Condition"><SelectField id="g-cond" testid="g-cond" value={s.condition} onChange={(v) => setS({ ...s, condition: v })} options={["New", "Like new", "Good", "Fair"]} placeholder="Condition" /></Field>
                <Field id="g-loc" label="Collection location" required><TextInput id="g-loc" testid="g-loc" required value={s.collection_location} onChange={(v) => setS({ ...s, collection_location: v })} /></Field>
                <Field id="g-cm" label="Preferred contact"><SelectField id="g-cm" testid="g-cm" value={s.contact_method} onChange={(v) => setS({ ...s, contact_method: v })} options={["Email", "Phone", "WhatsApp"]} placeholder="Method" /></Field>
            </div>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="g-consent" />
            {error && <p className="text-sm text-red-600" data-testid="g-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="g-submit">{submitting ? "Submitting…" : "Offer Goods or Equipment"}</button>
        </form>
    );
}

/* ---------------- Corporate ---------------- */
function CorporatePanel() {
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div>
                <PanelHeader eyebrow="Corporate & Institutional" title="Partner With Global Seed Foundation">Organisations can support sustainable community development through strategic partnerships and programme sponsorship.</PanelHeader>
                <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-[color:var(--gsf-body)]" data-testid="corporate-examples">
                    {["Women Empowerment", "Youth Empowerment", "Community Enterprises", "Disability Inclusion", "Skills Development", "Community Infrastructure", "Community Listening Forums"].map((x) => <li key={x}>• {x}</li>)}
                </ul>
            </div>
            <CorporateForm />
        </div>
    );
}
function CorporateForm() {
    const [s, setS] = useState({ organisation: "", contact_person: "", position: "", email: "", phone: "", partnership_interest: "", message: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("corporate_partnership");
    if (done) return <SuccessCard testid="corporate-success">Thank you. Our partnerships team will be in touch.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-6 space-y-4" data-testid="corporate-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="c-org" label="Organisation" required><TextInput id="c-org" testid="c-org" required value={s.organisation} onChange={(v) => setS({ ...s, organisation: v })} /></Field>
                <Field id="c-cp" label="Contact person" required><TextInput id="c-cp" testid="c-cp" required value={s.contact_person} onChange={(v) => setS({ ...s, contact_person: v })} /></Field>
                <Field id="c-pos" label="Position"><TextInput id="c-pos" testid="c-pos" value={s.position} onChange={(v) => setS({ ...s, position: v })} /></Field>
                <Field id="c-email" label="Email" required><TextInput id="c-email" testid="c-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="c-phone" label="Phone"><TextInput id="c-phone" testid="c-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
                <Field id="c-int" label="Partnership interest" required><SelectField id="c-int" testid="c-int" value={s.partnership_interest} onChange={(v) => setS({ ...s, partnership_interest: v })} options={PROGRAMMES} placeholder="Select area" /></Field>
            </div>
            <Field id="c-msg" label="Message"><TextArea id="c-msg" testid="c-msg" rows={3} value={s.message} onChange={(v) => setS({ ...s, message: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="c-consent" />
            {error && <p className="text-sm text-red-600" data-testid="c-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="c-submit">{submitting ? "Submitting…" : "Discuss a Partnership"}</button>
        </form>
    );
}

/* ---------------- Pledge ---------------- */
function PledgePanel() {
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div>
                <PanelHeader eyebrow="Donation Pledge" title="I'd Like to Support">Not ready to donate today? Let us know your interest and our team will contact you when additional donation options become available.</PanelHeader>
            </div>
            <PledgeForm />
        </div>
    );
}
function PledgeForm() {
    const [s, setS] = useState({ name: "", email: "", phone: "", estimated_contribution: "", preferred_method: "EcoCash", programme_of_interest: "", comments: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("donation_pledge");
    if (done) return <SuccessCard testid="pledge-success">Thank you. Our team will be in touch when additional donation channels become available.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-6 space-y-4" data-testid="pledge-form">
            <Field id="p-name" label="Name" required><TextInput id="p-name" testid="p-name" required value={s.name} onChange={(v) => setS({ ...s, name: v })} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="p-email" label="Email" required><TextInput id="p-email" testid="p-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="p-phone" label="Phone"><TextInput id="p-phone" testid="p-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
                <Field id="p-amt" label="Estimated contribution"><TextInput id="p-amt" testid="p-amt" placeholder="e.g. USD 100" value={s.estimated_contribution} onChange={(v) => setS({ ...s, estimated_contribution: v })} /></Field>
                <Field id="p-method" label="Preferred method"><SelectField id="p-method" testid="p-method" value={s.preferred_method} onChange={(v) => setS({ ...s, preferred_method: v })} options={["EcoCash", "Goods", "Volunteer time", "Bank transfer (future)", "Card payment (future)"]} placeholder="Method" /></Field>
            </div>
            <Field id="p-prog" label="Programme of interest"><SelectField id="p-prog" testid="p-prog" value={s.programme_of_interest} onChange={(v) => setS({ ...s, programme_of_interest: v })} options={PROGRAMMES} placeholder="Programme" /></Field>
            <Field id="p-msg" label="Comments"><TextArea id="p-msg" testid="p-msg" rows={3} value={s.comments} onChange={(v) => setS({ ...s, comments: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="p-consent" />
            {error && <p className="text-sm text-red-600" data-testid="p-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="p-submit">{submitting ? "Submitting…" : "Make a Donation Pledge"}</button>
        </form>
    );
}

/* ---------------- Online Card Payments (Coming Soon) ---------------- */
function OnlinePaymentsPanel() {
    return (
        <div className="max-w-3xl">
            <PanelHeader eyebrow="Online Card Payments" title="Coming soon">Secure Visa, Mastercard and additional online payment options will be available once Global Seed Foundation completes its dedicated organisational merchant account and payment gateway integration.</PanelHeader>
            <div className="mt-6 gsf-card p-8 flex items-start gap-4" data-testid="online-payments-card">
                <Lock size={28} className="text-[color:var(--gsf-gold-600)]" />
                <div>
                    <div className="font-semibold text-[color:var(--gsf-charcoal)]">Preparing secure online payments</div>
                    <p className="mt-2 text-sm text-[color:var(--gsf-body)]">
                        We're establishing dedicated organisational banking and merchant facilities. In the meantime,
                        supporters can use EcoCash, volunteer time, in-kind donations, corporate partnerships or a
                        donation pledge.
                    </p>
                </div>
            </div>
        </div>
    );
}
